import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import React, { useCallback, useMemo, useState } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';

import type {
  IOneItemInPrepareForRequest,
  ITodayOffersForRequest,
} from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';
import LoadingStage from '@/pages/PurchasesContainer/RequestPerOffer/components/LoadingStage';

import { PrepareOfferForRequestApi } from './api/OffersRequest.api';
import { InitialStage } from './components/InitialStage';
import { PrepareItemsDialog } from './components/PrepareRequest';

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center p-6 text-center">
      <h2 className="mb-4 text-xl font-semibold text-red-600">حدث خطأ ما</h2>
      <p className="mb-4 text-gray-700">{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        className="rounded-md bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
      >
        إعادة المحاولة
      </button>
    </div>
  );
}

function NewRequestPerOffersPage() {
  const queryClient = useQueryClient();

  // State management with atomic updates
  const [selectedSupplier, setSelectedSupplier] =
    useState<null | ITodayOffersForRequest>(null);

  const [loadingStage, setLoadingStage] = useState<{
    isLoading: boolean;
    type: 'upload' | 'check' | 'update' | 'main';
  }>({
    isLoading: false,
    type: 'main',
  });

  const [stage, setStage] = useState(0);
  const [prepareItems, setPrepareItems] = useState<
    IOneItemInPrepareForRequest[]
  >([]);

  // Memoized handlers for better performance
  const handleSelectSupplier = useCallback(
    (newValue: ITodayOffersForRequest | null) => {
      setSelectedSupplier(newValue);
    },
    [],
  );

  const handleLoadingStage = useCallback(
    (value: boolean, type: 'check' | 'upload' | 'update' | 'main') => {
      setLoadingStage({
        isLoading: value,
        type,
      });
    },
    [],
  );

  const handleStage = useCallback((value: number) => {
    setStage(value);
  }, []);

  const handlePrepareItems = useCallback(
    (value: IOneItemInPrepareForRequest[]) => {
      setPrepareItems(value);
    },
    [],
  );

  const handleUpdateOneItemLink = useCallback(
    (item: IOneItemInPrepareForRequest, _skipResetPage?: boolean) => {
      // We're only receiving the item update here,
      // The actual pagination reset prevention is handled in the table component
      // via the table.options.meta.skipAutoResetPageIndex function
      setPrepareItems((prevItems) => {
        const allItems = [...prevItems];
        const itemIndex = allItems.findIndex(
          (i) => i.itemBodyID?.toString() === item.itemBodyID?.toString(),
        );

        if (itemIndex !== -1) {
          allItems[itemIndex] = item;
        }

        return allItems;
      });
      handleLoadingStage(false, 'update');
    },
    [handleLoadingStage],
  );

  const handleAddOneItemLink = useCallback(
    (item: IOneItemInPrepareForRequest) => {
      setPrepareItems((prevItems) => [...prevItems, item]);
      handleLoadingStage(false, 'update');
    },
    [handleLoadingStage],
  );

  const handleRestPrepareItems = useCallback(() => {
    setPrepareItems([]);
  }, []);

  const closeDialogFinalFn = useCallback(() => {
    handleStage(0);
    setSelectedSupplier(null);
    handleRestPrepareItems();
  }, [handleStage, handleRestPrepareItems]);

  const handleResetStage = useCallback(() => {
    handleStage(0);
    setSelectedSupplier(null);
    handleRestPrepareItems();
  }, [handleStage, handleRestPrepareItems]);

  // Optimized mutation with proper cache handling
  const addItemsPrepareMutation = useMutation({
    mutationFn: PrepareOfferForRequestApi,
    onSuccess: (data) => {
      handlePrepareItems(data);
      handleStage(1);
      handleLoadingStage(false, 'main');

      // Update any relevant queries
      queryClient.invalidateQueries({ queryKey: ['prepareOffers'] });

      toast.success('تم تجهيز عرض الشراء لعمل الطلبية', {
        position: 'top-right',
        duration: 3000,
      });
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      handleLoadingStage(false, 'main');
      handleSelectSupplier(null);
      toast.error(
        `${err?.response?.data?.message || 'حدث خطأ أثناء التجهيز'}`,
        {
          position: 'top-right',
          duration: 4000,
        },
      );
    },
  });

  const OneOfferSelectForPrepareFn = useCallback(
    async (offer: ITodayOffersForRequest) => {
      if (!offer?._id) return;

      try {
        handleSelectSupplier(offer);
        handleLoadingStage(true, 'check');
        await addItemsPrepareMutation.mutateAsync(offer?._id);
      } catch {
        // Error handling is in mutation onError
      }
    },
    [addItemsPrepareMutation, handleLoadingStage, handleSelectSupplier],
  );

  // Memoize title to prevent unnecessary rerenders
  const pageTitle = useMemo(
    () => `${selectedSupplier?.ClientName || '-'} طلبية شراء عروض `,
    [selectedSupplier?.ClientName],
  );

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => closeDialogFinalFn()}
    >
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <div className="h-[90vh]">
        {loadingStage?.isLoading && (
          <LoadingStage
            LoadingType={loadingStage?.type}
            loading={loadingStage?.isLoading}
            supplier={selectedSupplier?.ClientName}
          />
        )}

        {stage === 0 && (
          <InitialStage
            OneOfferSelectForPrepareFn={OneOfferSelectForPrepareFn}
          />
        )}

        {stage === 1 && selectedSupplier && (
          <PrepareItemsDialog
            isOpen={true}
            onClose={() => handleResetStage()}
            items={prepareItems}
            setItems={setPrepareItems}
            handleResetStage={handleResetStage}
            client={selectedSupplier}
            handleUpdateOneItemLink={handleUpdateOneItemLink}
            handleLoadingStage={handleLoadingStage}
            isLoading={loadingStage?.isLoading}
            closeDialogFinalFn={closeDialogFinalFn}
            handleAddOneItemLink={handleAddOneItemLink}
          />
        )}
      </div>
    </ErrorBoundary>
  );
}

export default React.memo(NewRequestPerOffersPage);
